import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deletePurchaseOrderPaymentTransaction, fetchPurchaseOrderDetail, fetchPurchaseOrderFinanceDetail, updatePurchaseOrderGeneralDetail, updatePurchaseOrderPaymentTransactions, updatePurchaseOrderVerification } from "../../../store/Slices/ProcurementSlice";
import { Col, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomTabs from "../../../components/CustomTabs";
import Select from "../../../components/Select";
import Input from "../../../components/input";
import NewTableComponent from "../../../components/NewTableComponent";
import { detailsIcon, activeDetailsIcon, inspectionIcon, activeInspectionIcon, coinsStackDarkIcon, coinsStackActiveIcon } from "../../../utils/svgs";
import { FiCalendar, FiEdit, FiTrash2 } from "react-icons/fi";
import CustomModal from "../../../components/CustomModal";
import FileUpload from "../../../components/FileUpload";
import "../../../styles/PODetails.css";
import PermissionWrapper from '../../../components/PermissionWrapper';
import { AppMainModules, InventorySubModules, ProcurementPermission } from '../../../utils/app-constant';
import { successNotify, errorNotify } from "../../../utils/toast";
import { Formik } from "formik";
import { getWarehousesDropdown } from "../../../store/Slices/PartVendorSlice";
import { fetchBanksDropdown } from "../../../store/Slices/dropdownSlice";
import { POPaymentTransactionSchema } from "../../../utils/Schema";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import { apiService } from "../../../services/apiService";

const FILE_PATH_PATTERN = /\.(jpg|jpeg|png|gif|bmp|webp|svg|pdf|doc|docx|xls|xlsx|zip|rar|7z)$/i;

const looksLikeFilePath = (value) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return false;
    const sanitizedValue = normalizedValue.split(/[?#]/)[0];
    return FILE_PATH_PATTERN.test(sanitizedValue);
};


const findNestedAttachmentPath = (value, visited = new Set()) => {
    if (!value) return "";

    if (typeof value === "string") {
        return looksLikeFilePath(value) ? value.trim() : "";
    }

    if (typeof value !== "object") return "";
    if (visited.has(value)) return "";
    visited.add(value);

    const prioritizedEntries = [
        value?.data,
        value?.file,
        value?.files,
        value?.media,
        value?.attachments,
        value?.items,
        value?.rows,
        value?.results,
    ];

    for (const entry of prioritizedEntries) {
        const nestedPath = findNestedAttachmentPath(entry, visited);
        if (nestedPath) return nestedPath;
    }

    for (const entry of Object.values(value)) {
        const nestedPath = findNestedAttachmentPath(entry, visited);
        if (nestedPath) return nestedPath;
    }

    return "";
};

const getAttachmentPath = (file) => {
    if (!file) return "";
    if (typeof file === "string") return file;
    return (
        file?.file_path ||
        file?.fileName ||
        file?.file_name ||
        file?.file_url ||
        file?.full_url ||
        file?.media_url ||
        file?.path ||
        file?.url ||
        file?.src ||
        file?.document ||
        file?.file ||
        file?.image ||
        file?.data?.file_path ||
        file?.data?.fileName ||
        file?.data?.file_name ||
        file?.data?.file_url ||
        file?.data?.full_url ||
        file?.data?.media_url ||
        file?.data?.path ||
        file?.data?.url ||
        file?.data?.src ||
        file?.data?.document ||
        file?.data?.file ||
        file?.data?.image ||
        findNestedAttachmentPath(file) ||
        file?.originalName ||
        file?.original_name ||
        file?.name ||
        ""
    );
};

const getAttachmentId = (file) => {
    if (!file || typeof file !== "object") return "";

    const attachmentId =
        file?.gallery_id ||
        file?.galleryId ||
        file?.file_id ||
        file?.fileId ||
        file?.media_id ||
        file?.mediaId ||
        file?.delete_id ||
        file?.deleteId ||
        file?.id ||
        file?._id ||
        file?.data?.gallery_id ||
        file?.data?.galleryId ||
        file?.data?.file_id ||
        file?.data?.fileId ||
        file?.data?.media_id ||
        file?.data?.mediaId ||
        file?.data?.delete_id ||
        file?.data?.deleteId ||
        file?.data?.id ||
        file?.data?._id;

    return attachmentId === undefined || attachmentId === null
        ? ""
        : String(attachmentId).trim();
};

const normalizeAttachment = (file) => {
    const filePath = getAttachmentPath(file);
    if (!filePath) return null;
    if (typeof file === "string") {
        return { fileName: filePath, originalName: filePath };
    }
    return {
        ...file,
        fileName: file?.fileName || filePath,
        originalName: file?.originalName || filePath,
    };
};

const mergeUniqueAttachments = (files = []) => {
    const byKey = new Map();
    files.forEach((file) => {
        const normalized = normalizeAttachment(file);
        if (!normalized) return;
        const attachmentId = getAttachmentId(normalized);
        const mergeKey = attachmentId ? `id:${attachmentId}` : `path:${normalized.fileName}`;
        if (!byKey.has(mergeKey)) {
            byKey.set(mergeKey, normalized);
        }
    });
    return Array.from(byKey.values());
};

const getAttachmentMergeKey = (file) => {
    const normalized = normalizeAttachment(file);
    if (!normalized) return "";
    const attachmentId = getAttachmentId(normalized);
    return attachmentId ? `id:${attachmentId}` : `path:${normalized.fileName}`;
};

const getAttachmentMergeKeys = (files = []) =>
    markFinanceGeneralAttachments(files)
        .map((file) => getAttachmentMergeKey(file))
        .filter(Boolean);

const GENERAL_DETAIL_ATTACHMENT_SCOPE = "general_detail";
const markFinanceGeneralAttachments = (files = []) =>
    mergeUniqueAttachments(files).map((file) => ({
        ...file,
        attachmentScope:
            typeof file === "object" && file !== null && file?.attachmentScope
                ? file.attachmentScope
                : GENERAL_DETAIL_ATTACHMENT_SCOPE,
    }));

const collectAttachmentEntries = (value) => {
    const queue = Array.isArray(value) ? [...value] : [value];
    const collectedFiles = [];
    const visitedObjects = new Set();

    while (queue.length) {
        const current = queue.shift();

        if (current === null || current === undefined || current === "") {
            continue;
        }

        if (typeof current === "string") {
            if (current.trim()) {
                collectedFiles.push(current.trim());
            }
            continue;
        }

        if (typeof current !== "object") {
            continue;
        }

        if (visitedObjects.has(current)) {
            continue;
        }
        visitedObjects.add(current);

        if (getAttachmentPath(current)) {
            collectedFiles.push(current);
            continue;
        }

        [
            current?.data,
            current?.file,
            current?.files,
            current?.items,
            current?.rows,
            current?.media,
            current?.attachments,
            current?.results,
        ].forEach((entry) => {
            if (Array.isArray(entry)) {
                queue.push(...entry);
            } else if (entry) {
                queue.push(entry);
            }
        });
    }

    return collectedFiles;
};


const COST_BREAKDOWN_EPSILON = 0.0001;
const SUB_TOTAL_FIELD_KEYS = ["sub_total", "subtotal"];
const SHIPPING_FIELD_KEYS = ["total_shipping", "shipping", "freight", "shipping_charges"];
const TAX_PERCENT_FIELD_KEYS = ["total_tax", "tax", "tax_percent", "tax_percentage"];
const DISCOUNT_PERCENT_FIELD_KEYS = ["total_discount", "discount", "discount_percent", "discount_percentage"];
const TOTAL_FIELD_KEYS = ["total_amount", "total_payable", "grand_total"];
const LINKED_QUOTE_ID_FIELDS = ["rfq_vendor_quote_id", "parts_rfq_bids_id", "parts_rfq_bid_id", "selected_bid_id", "bid_id"];
const QUOTE_IDENTIFIER_FIELDS = ["id", "rfq_vendor_quote_id", "parts_rfq_bids_id", "parts_rfq_bid_id", "bid_id"];
const LINKED_QUOTE_OBJECT_PATHS = [
    ["vendor_quote"],
    ["rfq_vendor_quote"],
    ["quote"],
    ["selected_quote"],
    ["selected_bid"],
    ["bid"],
    ["rfq_bid"],
    ["parts_rfq_bid"],
    ["rfq", "vendor_quote"],
    ["rfq", "rfq_vendor_quote"],
    ["rfq", "quote"],
    ["rfq", "selected_quote"],
    ["rfq", "selected_bid"],
    ["rfq", "bid"],
];

const toFiniteNumber = (value) => {
    if (value === null || value === undefined) return null;

    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value === "string") {
        const trimmedValue = value.trim();
        if (!trimmedValue) return null;

        const normalizedValue = trimmedValue.replace(/,/g, "");
        const directNumber = Number(normalizedValue);
        if (Number.isFinite(directNumber)) return directNumber;

        const numberMatch = normalizedValue.match(/-?\d+(\.\d+)?/);
        if (!numberMatch) return null;

        const parsedMatch = Number(numberMatch[0]);
        return Number.isFinite(parsedMatch) ? parsedMatch : null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const roundAmount = (value) => {
    const numericValue = toFiniteNumber(value);
    if (numericValue === null) return 0;
    return Number(numericValue.toFixed(2));
};

const getValueAtPath = (source, path = []) => path.reduce((acc, key) => acc?.[key], source);

const readNumericField = (source, keys = []) => {
    if (!source || typeof source !== "object") return null;

    for (const key of keys) {
        const parsedValue = toFiniteNumber(source?.[key]);
        if (parsedValue !== null) return parsedValue;
    }

    return null;
};

const readStringId = (source, keys = []) => {
    for (const key of keys) {
        const rawValue = source?.[key];
        if (rawValue === undefined || rawValue === null) continue;
        const normalizedValue = String(rawValue).trim();
        if (normalizedValue) return normalizedValue;
    }
    return null;
};

const hasNonZeroValue = (value) =>
    value !== null && Math.abs(value) > COST_BREAKDOWN_EPSILON;

const getLinkedQuote = (entity) => {
    for (const path of LINKED_QUOTE_OBJECT_PATHS) {
        const candidate = getValueAtPath(entity, path);
        if (candidate && typeof candidate === "object") return candidate;
    }

    const quoteCollections = [
        entity?.quotes,
        entity?.bids,
        entity?.vendor_quotes,
        entity?.rfq_vendor_quotes,
        entity?.rfq?.quotes,
        entity?.rfq?.bids,
        entity?.rfq?.vendor_quotes,
        entity?.rfq?.rfq_vendor_quotes,
    ]
        .filter(Array.isArray)
        .flat();

    if (!quoteCollections.length) return null;

    const linkedQuoteId = readStringId(entity, LINKED_QUOTE_ID_FIELDS);
    if (linkedQuoteId) {
        const matchedQuote = quoteCollections.find((quote) => {
            const quoteId = readStringId(quote, QUOTE_IDENTIFIER_FIELDS);
            return quoteId === linkedQuoteId;
        });
        if (matchedQuote) return matchedQuote;
    }

    return quoteCollections.find((quote) => quote && typeof quote === "object") || null;
};

const resolveLinkedQuoteTotal = (entity) => {
    const linkedQuote = getLinkedQuote(entity);
    if (!linkedQuote) return null;

    const explicitTotal = readNumericField(linkedQuote, TOTAL_FIELD_KEYS);
    if (explicitTotal !== null) return roundAmount(explicitTotal);

    const subTotal = readNumericField(linkedQuote, SUB_TOTAL_FIELD_KEYS) ?? readNumericField(entity, SUB_TOTAL_FIELD_KEYS);
    const totalShipping = readNumericField(linkedQuote, SHIPPING_FIELD_KEYS) ?? readNumericField(entity, SHIPPING_FIELD_KEYS);
    const taxPercent = readNumericField(linkedQuote, TAX_PERCENT_FIELD_KEYS) ?? readNumericField(entity, TAX_PERCENT_FIELD_KEYS);
    const discountPercent = readNumericField(linkedQuote, DISCOUNT_PERCENT_FIELD_KEYS) ?? readNumericField(entity, DISCOUNT_PERCENT_FIELD_KEYS);

    if (subTotal === null && totalShipping === null && taxPercent === null && discountPercent === null) {
        return null;
    }

    const safeSubTotal = subTotal ?? 0;
    const safeShipping = totalShipping ?? 0;
    const safeTaxPercent = taxPercent ?? 0;
    const safeDiscountPercent = discountPercent ?? 0;

    const totalTax = (safeSubTotal * safeTaxPercent) / 100;
    const totalDiscount = (safeSubTotal * safeDiscountPercent) / 100;
    return roundAmount(safeSubTotal + safeShipping + totalTax - totalDiscount);
};

const formatCostInput = (value) => {
    const numericValue = toFiniteNumber(value);
    if (numericValue === null) return "0";
    return String(Number(numericValue.toFixed(2)));
};

const EMPTY_PAYMENT_TRANSACTION_FORM_VALUES = {
    amount: "",
    payment_method: "",
    bank_name: "",
    account_title: "",
    iban_number: "",
    reference_number: "",
    files: [],
    fileObjects: [],
};

const normalizePaymentMethodValue = (value = "") => {
    const normalizedValue = String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");

    if (normalizedValue === "bank_transfer") return "bank_transfer";
    if (normalizedValue === "cash") return "cash";
    if (normalizedValue === "cheque" || normalizedValue === "check") return "cheque";
    return "";
};

const getPaymentMethodLabel = (value = "") => {
    const normalizedValue = normalizePaymentMethodValue(value);

    if (normalizedValue === "bank_transfer") return "Bank Transfer";
    if (normalizedValue === "cash") return "Cash";
    if (normalizedValue === "cheque") return "Cheque";

    return String(value || "").trim();
};

const getPaymentTransactionFiles = (transaction) => mergeUniqueAttachments([
    ...(Array.isArray(transaction?.po_transactions_files) ? transaction.po_transactions_files : []),
    ...(Array.isArray(transaction?.files) ? transaction.files : []),
    ...(Array.isArray(transaction?.attachments) ? transaction.attachments : []),
]);

const buildPaymentTransactionFormValues = (transaction) => {
    if (!transaction) return { ...EMPTY_PAYMENT_TRANSACTION_FORM_VALUES };

    const fileObjects = getPaymentTransactionFiles(transaction);

    return {
        amount: formatCostInput(transaction?.amount ?? 0),
        payment_method: normalizePaymentMethodValue(transaction?.payment_method),
        bank_name: transaction?.bank_name || transaction?.bank?.name || "",
        account_title: transaction?.account_title || "",
        iban_number: transaction?.iban_number || "",
        reference_number: transaction?.reference_number || "",
        files: fileObjects
            .map((file) => file?.fileName || file?.name || file?.file_path)
            .filter(Boolean),
        fileObjects,
    };
};

const getPaymentTransactionsTotal = (transactions = []) =>
    transactions.reduce((sum, transaction) => sum + (toFiniteNumber(transaction?.amount) ?? 0), 0);

const getUploadedFileNames = (files = [], allowedScopes = []) =>
    mergeUniqueAttachments(
        (Array.isArray(files) ? files : []).filter((file) => {
            if (!allowedScopes.length) return true;
            const attachmentScope =
                typeof file === "object" && file !== null
                    ? (file?.attachmentScope || GENERAL_DETAIL_ATTACHMENT_SCOPE)
                    : GENERAL_DETAIL_ATTACHMENT_SCOPE;
            return allowedScopes.includes(attachmentScope);
        })
    )
        .map((file) => file?.fileName || file?.name || file?.file_path)
        .filter(Boolean);

const PODetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const requiredStar = <span className="required_star">*</span>;
    const {
        poDetail,
        poFinanceDetail,
        poDetailLoading,
        updatePurchaseOrderGeneralDetailLoading,
        updatePurchaseOrderVerificationLoading,
        updatePurchaseOrderPaymentTransactionsLoading,
        deletePurchaseOrderPaymentTransactionLoading,
    } = useSelector((state) => state.procurement);
    const { warehousesDropdownData } = useSelector((state) => state.partVendors);
    const { banksDropdownData } = useSelector((state) => state.dropdowns);
    const [exportPdfLoading, setExportPdfLoading] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(fetchPurchaseOrderDetail(id));
            dispatch(fetchPurchaseOrderFinanceDetail(id));
        }
    }, [dispatch, id]);
    useEffect(() => {
        dispatch(getWarehousesDropdown());
        dispatch(fetchBanksDropdown());
    }, [dispatch]);

    const [activeTab, setActiveTab] = useState("general");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchFilters, setSearchFilters] = useState({});
    const [attachments, setAttachments] = useState([]);
    const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [paymentTransactions, setPaymentTransactions] = useState([]);
    const [hasLocalPaymentTransactionChanges, setHasLocalPaymentTransactionChanges] = useState(false);
    const [showDeleteTransactionModal, setShowDeleteTransactionModal] = useState(false);
    const [transactionToDelete, setTransactionToDelete] = useState(null);
    const [deliveryErrors, setDeliveryErrors] = useState({});
    const [hasLocalDeliveryChanges, setHasLocalDeliveryChanges] = useState(false);
    const [hasLocalFinanceAttachmentChanges, setHasLocalFinanceAttachmentChanges] = useState(false);
    const [removedFinanceAttachmentKeys, setRemovedFinanceAttachmentKeys] = useState([]);
    const [financeUploadDate, setFinanceUploadDate] = useState("");
    const [liveDownAmount, setLiveDownAmount] = useState("0");
    const [costInputs, setCostInputs] = useState({
        shipping: "0",
        taxPercent: "0",
        discountPercent: "0",
    });
    const [formInitialValues, setFormInitialValues] = useState({
        po_id: id || "",
        notes: "",
        receiving_notes: "",
        total_down_amount: "0",
    });
    const isPoVerified = poDetail?.status === "verified";

    const handlePartDetailsNavigate = (partId) => {
        if (!partId) return;
        navigate(`/parts/details/${partId}`);
    };

    const navigateToPurchaseOrders = (replace = false) => {
        navigate("/procurement?tab=PO", replace ? { replace: true } : undefined);
    };

    const handleExportPdf = async () => {
        const poId = id || poDetail?.id || poDetail?.parts_purchase_order_id;
        if (!poId) {
            errorNotify("Purchase order id not found");
            return;
        }

        setExportPdfLoading(true);
        try {
            const response = await apiService.getPurchaseOrderExportPdf(poId);
            const responseData = response?.data ?? response;
            const filePath =
                responseData?.url ||
                responseData?.file ||
                (typeof responseData === "string" ? responseData : null);

            if (!filePath) {
                errorNotify("PDF file not found");
                return;
            }

            const fileUrl = /^https?:\/\//i.test(filePath)
                ? filePath
                : `${process.env.REACT_APP_API_FILES_URL}${filePath}`;

            const link = document.createElement("a");
            link.href = fileUrl;
            link.target = "_blank";
            link.setAttribute("download", `purchase-order-${poId}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            errorNotify(error || "Failed to export purchase order PDF");
        } finally {
            setExportPdfLoading(false);
        }
    };


    // Map API items to partsData structure
    // Map API items to partsData structure
    const partsData = useMemo(() => (poDetail?.items?.map(item => ({
        id: Number(item.id || 0),
        parts_purchase_order_item_id: item.parts_purchase_order_item_id,
        name: item.part?.name || "Unknown Part",
        qty: item.ordered_quantity,
        unitPrice: item.unit_price,
        total: item.line_total,
        ...item
    })) || []), [poDetail?.items]);

    // Options for Select components
    const warehouseOptions = warehousesDropdownData || [];

    const conditionOptions = [
        { label: "Good", value: "good" },
        { label: "Damaged", value: "damaged" },
        { label: "Incorrect", value: "incorrect" },
    ];
    const paymentMethodOptions = [
        { label: "Cash", value: "cash" },
        { label: "Bank Transfer", value: "bank_transfer" },
        { label: "Cheque", value: "cheque" },
    ];

    const getConditionStyles = (conditionValue) => {
        let bg = 'transparent';
        let color = '#000';

        if (conditionValue === 'good') {
            bg = '#DCFCE7'; // Green-100 like
            color = '#166534'; // Green-700 like
        } else if (conditionValue === 'damaged') {
            bg = '#FEE2E2'; // Red-100 like
            color = '#991B1B'; // Red-700 like
        } else if (conditionValue === 'incorrect') {
            bg = '#E2E3E8'; // Gray
            color = '#3D3F5C';
        }

        return {
            control: (baseStyles) => ({
                ...baseStyles,
                background: bg,
                border: '1px solid transparent',
                color: color,
                minHeight: '36px',
                borderRadius: '6px',
                boxShadow: 'none',
            }),
            singleValue: (baseStyles) => ({
                ...baseStyles,
                color: color,
                fontWeight: 500,
                fontSize: '13px'
            }),
            dropdownIndicator: (baseStyles) => ({
                ...baseStyles,
                color: color,
                padding: '0 8px',
            }),
            indicatorSeparator: () => ({ display: 'none' }),
            container: (baseStyles) => ({
                ...baseStyles,
                padding: 0,
            })
        };
    };

    // State for Delivery Table Data - Sync with partsData when loaded
    const [deliveryTableData, setDeliveryTableData] = useState([]);

    useEffect(() => {
        if (partsData.length > 0) {
            setDeliveryTableData(partsData.map(part => ({
                ...part,
                qtyReceived: sanitizeNonNegative(part.actual_quantity ?? part.ordered_quantity ?? 0),
                goodQty: sanitizeNonNegative(part.good_quantity ?? 0),
                damagedQty: sanitizeNonNegative(part.damaged_quantity ?? 0),
                incorrectQty: sanitizeNonNegative(part.incorrect_quantity ?? 0),
                warehouse: part.warehouse_id || "",
                condition: part.condition || "good" // Updated mapping
            })));
            setDeliveryErrors({});
            setHasLocalDeliveryChanges(false);

        }
    }, [poDetail, partsData]);

    useEffect(() => {
        const initialDownAmount = formatCostInput(
            poFinanceDetail?.total_down_amount ?? poDetail?.total_down_amount ?? 0
        );

        setFormInitialValues({
            po_id: poDetail?.id || id || poDetail?.parts_purchase_order_id || poDetail?.po_id || "",
            notes: poDetail?.notes || "",
            receiving_notes: poDetail?.receiving_notes || "",
            total_down_amount: initialDownAmount,
        });
        setLiveDownAmount(initialDownAmount);
    }, [poDetail, poFinanceDetail?.total_down_amount, id]);

    useEffect(() => {
        if (!id) return;

        const linkedQuote = getLinkedQuote(poDetail);
        const poTotalPayable = toFiniteNumber(poDetail?.total_payable);
        const linkedQuoteTotal = resolveLinkedQuoteTotal(poDetail);
        const shouldUseQuoteFallback =
            linkedQuoteTotal !== null &&
            linkedQuoteTotal > 0 &&
            (poTotalPayable === null || Math.abs(poTotalPayable - linkedQuoteTotal) > 0.01);

        const shippingFromPo = readNumericField(poDetail, SHIPPING_FIELD_KEYS);
        const taxPercentFromPo = readNumericField(poDetail, TAX_PERCENT_FIELD_KEYS);
        const discountPercentFromPo = readNumericField(poDetail, DISCOUNT_PERCENT_FIELD_KEYS);

        const shippingFromQuote = readNumericField(linkedQuote, SHIPPING_FIELD_KEYS);
        const taxPercentFromQuote = readNumericField(linkedQuote, TAX_PERCENT_FIELD_KEYS);
        const discountPercentFromQuote = readNumericField(linkedQuote, DISCOUNT_PERCENT_FIELD_KEYS);

        const poBreakdownHasAnyValue = [shippingFromPo, taxPercentFromPo, discountPercentFromPo].some(hasNonZeroValue);
        const quoteBreakdownHasAnyValue = [shippingFromQuote, taxPercentFromQuote, discountPercentFromQuote].some(hasNonZeroValue);
        const shouldPreferQuoteBreakdown =
            quoteBreakdownHasAnyValue &&
            (shouldUseQuoteFallback || !poBreakdownHasAnyValue);

        const totalShipping = shouldPreferQuoteBreakdown
            ? (shippingFromQuote ?? shippingFromPo ?? 0)
            : (shippingFromPo ?? shippingFromQuote ?? 0);
        const totalTax = shouldPreferQuoteBreakdown
            ? (taxPercentFromQuote ?? taxPercentFromPo ?? 0)
            : (taxPercentFromPo ?? taxPercentFromQuote ?? 0);
        const totalDiscount = shouldPreferQuoteBreakdown
            ? (discountPercentFromQuote ?? discountPercentFromPo ?? 0)
            : (discountPercentFromPo ?? discountPercentFromQuote ?? 0);

        setCostInputs({
            shipping: formatCostInput(totalShipping),
            taxPercent: formatCostInput(totalTax),
            discountPercent: formatCostInput(totalDiscount),
        });
    }, [
        id,
        poDetail,
        poDetail?.id,
        poDetail?.updated_at,
        poDetail?.total_payable,
        poDetail?.total_shipping,
        poDetail?.total_tax,
        poDetail?.total_discount,
        poDetail?.vendor_quote,
        poDetail?.rfq_vendor_quote,
    ]);

    useEffect(() => {
        if (poFinanceDetail) {
            const rawFiles = collectAttachmentEntries([
                poFinanceDetail?.files,
                poFinanceDetail?.attachments,
            ]);
            const serverAttachments = markFinanceGeneralAttachments(rawFiles);
            const removedAttachmentKeySet = new Set(removedFinanceAttachmentKeys);
            const visibleServerAttachments = serverAttachments.filter(
                (file) => !removedAttachmentKeySet.has(getAttachmentMergeKey(file))
            );

            setAttachments((prevAttachments) => {
                const normalizedPreviousAttachments = markFinanceGeneralAttachments(prevAttachments).filter(
                    (file) => !removedAttachmentKeySet.has(getAttachmentMergeKey(file))
                );

                if (!hasLocalFinanceAttachmentChanges) {
                    return visibleServerAttachments;
                }

                return markFinanceGeneralAttachments([
                    ...visibleServerAttachments,
                    ...normalizedPreviousAttachments,
                ]);
            });
        }
    }, [poFinanceDetail, hasLocalFinanceAttachmentChanges, removedFinanceAttachmentKeys]);

    useEffect(() => {
        if (!poFinanceDetail || !hasLocalFinanceAttachmentChanges) return;

        const rawFiles = collectAttachmentEntries([
            poFinanceDetail?.files,
            poFinanceDetail?.attachments,
        ]);
        const serverAttachments = markFinanceGeneralAttachments(rawFiles);
        const serverAttachmentKeys = new Set(
            serverAttachments.map((file) => getAttachmentMergeKey(file)).filter(Boolean)
        );

        const localAttachmentKeys = getAttachmentMergeKeys(attachments);
        const pendingRemovedAttachmentKeys = removedFinanceAttachmentKeys.filter((key) =>
            serverAttachmentKeys.has(key)
        );
        const hasPendingLocalAttachmentUploads = localAttachmentKeys.some(
            (key) => !serverAttachmentKeys.has(key)
        );

        if (!hasPendingLocalAttachmentUploads && pendingRemovedAttachmentKeys.length === 0) {
            if (removedFinanceAttachmentKeys.length > 0) {
                setRemovedFinanceAttachmentKeys([]);
            }
            setHasLocalFinanceAttachmentChanges(false);
        }
    }, [attachments, poFinanceDetail, hasLocalFinanceAttachmentChanges, removedFinanceAttachmentKeys]);

    const applyFinanceAttachmentUpdate = (updater) => {
        setHasLocalFinanceAttachmentChanges(true);
        setAttachments((prevAttachments) => {
            const nextAttachments =
                typeof updater === "function" ? updater(prevAttachments) : updater;
            const normalizedPreviousAttachments = markFinanceGeneralAttachments(prevAttachments);
            const normalizedNextAttachments = markFinanceGeneralAttachments(nextAttachments);
            const previousAttachmentKeys = new Set(
                normalizedPreviousAttachments
                    .map((file) => getAttachmentMergeKey(file))
                    .filter(Boolean)
            );
            const nextAttachmentKeys = new Set(
                normalizedNextAttachments
                    .map((file) => getAttachmentMergeKey(file))
                    .filter(Boolean)
            );

            setRemovedFinanceAttachmentKeys((prevRemovedKeys) => {
                const nextRemovedKeys = new Set(prevRemovedKeys);

                previousAttachmentKeys.forEach((key) => {
                    if (!nextAttachmentKeys.has(key)) {
                        nextRemovedKeys.add(key);
                    }
                });

                nextAttachmentKeys.forEach((key) => {
                    nextRemovedKeys.delete(key);
                });

                return Array.from(nextRemovedKeys);
            });

            return normalizedNextAttachments;
        });
    };


    const sanitizeNonNegative = (value) => {
        if (value === undefined || value === null || value === "") return "0";
        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed < 0) return "0";
        return String(parsed);
    };

    const resolveNumericPoId = (...candidates) => {
        for (const candidate of candidates) {
            if (candidate === undefined || candidate === null) continue;
            const raw = String(candidate).trim();
            if (!raw) continue;

            const direct = Number(raw);
            if (Number.isFinite(direct) && direct > 0) return direct;

            const digitsMatch = raw.match(/\d+/);
            if (digitsMatch) {
                const fromDigits = Number(digitsMatch[0]);
                if (Number.isFinite(fromDigits) && fromDigits > 0) return fromDigits;
            }
        }
        return null;
    };
    const serverPaymentTransactions = useMemo(
        () => Array.isArray(poFinanceDetail?.payment_transactions) ? poFinanceDetail.payment_transactions : [],
        [poFinanceDetail?.payment_transactions]
    );

    useEffect(() => {
        setPaymentTransactions(serverPaymentTransactions);
        setHasLocalPaymentTransactionChanges(false);
    }, [serverPaymentTransactions]);

    const closePaymentTransactionModal = () => {
        setShowAddTransactionModal(false);
        setEditingTransaction(null);
    };

    const handleOpenAddTransactionModal = () => {
        if (currentOutstandingAmount <= 0) {
            errorNotify("Add Transaction is disabled because there is no outstanding amount remaining.");
            return;
        }
        setEditingTransaction(null);
        setShowAddTransactionModal(true);
    };

    const handleOpenEditTransactionModal = (transaction) => {
        setEditingTransaction(transaction || null);
        setShowAddTransactionModal(true);
    };

    const closeDeleteTransactionModal = () => {
        setShowDeleteTransactionModal(false);
        setTransactionToDelete(null);
    };

    const handleOpenDeleteTransactionModal = (transaction) => {
        setTransactionToDelete(transaction || null);
        setShowDeleteTransactionModal(true);
    };

    const isEditingPaymentTransaction = Boolean(editingTransaction?.id);
    const paymentTransactionModalInitialValues = useMemo(
        () => buildPaymentTransactionFormValues(editingTransaction),
        [editingTransaction]
    );

    const upcomingTransactionLabel = useMemo(() => {
        const transactionIds = paymentTransactions
            .map((transaction) => Number(transaction?.id))
            .filter((value) => Number.isFinite(value) && value > 0);

        if (transactionIds.length > 0) {
            return `T-${Math.max(...transactionIds) + 1}`;
        }

        return "T-1";
    }, [paymentTransactions]);

    const formatFinanceAmount = (value) => {
        const numericValue = Number(value || 0);
        if (!Number.isFinite(numericValue)) return "0";
        return numericValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const sanitizeCostInput = (value) => {
        if (value === "") return "";
        if (!/^\d*\.?\d*$/.test(value)) return null;
        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed < 0) return "0";
        return value;
    };

    const handleCostInputChange = (field, value) => {
        const sanitized = sanitizeCostInput(value);
        if (sanitized === null) return;
        if ((field === "taxPercent" || field === "discountPercent") && sanitized !== "") {
            const asNumber = Number(sanitized);
            if (Number.isFinite(asNumber) && asNumber > 100) return;
        }
        setCostInputs((prev) => ({ ...prev, [field]: sanitized }));
    };

    const hasPersistedDeliveryBreakdown = useMemo(
        () =>
            (Array.isArray(poDetail?.items) ? poDetail.items : []).some((item) => {
                const goodQuantity = toFiniteNumber(item?.good_quantity);
                const damagedQuantity = toFiniteNumber(item?.damaged_quantity);
                const incorrectQuantity = toFiniteNumber(item?.incorrect_quantity);

                return (
                    (goodQuantity !== null && goodQuantity > 0) ||
                    (damagedQuantity !== null && damagedQuantity > 0) ||
                    (incorrectQuantity !== null && incorrectQuantity > 0)
                );
            }),
        [poDetail?.items]
    );

    const summaryValues = useMemo(() => {
        const linkedQuote = getLinkedQuote(poDetail);
        const shouldUseVerifiedSubtotal =
            activeTab === "delivery" ||
            hasLocalDeliveryChanges ||
            hasPersistedDeliveryBreakdown ||
            isPoVerified;

        const computedSubTotal = shouldUseVerifiedSubtotal && deliveryTableData.length > 0
            ? deliveryTableData.reduce((sum, row) => {
                const unitPrice = toFiniteNumber(row?.unitPrice ?? row?.unit_price) ?? 0;
                const goodQuantity = toFiniteNumber(row?.goodQty ?? row?.good_quantity) ?? 0;
                return sum + (unitPrice * goodQuantity);
            }, 0)
            : partsData.reduce((sum, row) => {
                const lineTotal = Number(row?.total ?? row?.line_total);
                if (Number.isFinite(lineTotal) && lineTotal > 0) {
                    return sum + lineTotal;
                }

                const unitPrice = Number(row?.unitPrice ?? row?.unit_price ?? 0);
                const quantity = Number(row?.qty ?? row?.ordered_quantity ?? 0);
                return sum + (unitPrice * quantity);
            }, 0);

        const quoteSubTotal = toFiniteNumber(linkedQuote?.sub_total);
        const fallbackSubTotal = Number(poFinanceDetail?.sub_total ?? poDetail?.sub_total ?? quoteSubTotal ?? 0);
        const subTotal =
            shouldUseVerifiedSubtotal && deliveryTableData.length > 0
                ? computedSubTotal
                : computedSubTotal > 0
                    ? computedSubTotal
                    : fallbackSubTotal;
        const totalShipping = Number(costInputs.shipping || 0);
        const taxPercent = Number(costInputs.taxPercent || 0);
        const discountPercent = Number(costInputs.discountPercent || 0);
        const totalTax = (subTotal * taxPercent) / 100;
        const totalDiscount = (subTotal * discountPercent) / 100;
        const totalPayable = subTotal + totalShipping + totalTax - totalDiscount;

        return {
            subTotal: Number(subTotal.toFixed(2)),
            totalShipping: Number(totalShipping.toFixed(2)),
            totalTax: Number(totalTax.toFixed(2)),
            totalDiscount: Number(totalDiscount.toFixed(2)),
            taxPercent: Number(taxPercent.toFixed(2)),
            discountPercent: Number(discountPercent.toFixed(2)),
            totalPayable: Number(totalPayable.toFixed(2)),
        };
    }, [
        activeTab,
        deliveryTableData,
        hasLocalDeliveryChanges,
        hasPersistedDeliveryBreakdown,
        isPoVerified,
        partsData,
        poDetail,
        poFinanceDetail?.sub_total,
        costInputs.shipping,
        costInputs.taxPercent,
        costInputs.discountPercent,
    ]);

    const financeBaseValues = useMemo(() => {
        const financeTotalPayable = toFiniteNumber(poFinanceDetail?.total_payable);
        const detailTotalPayable = toFiniteNumber(poDetail?.total_payable);
        const persistedTotalPaid = toFiniteNumber(poFinanceDetail?.total_paid_amount ?? poDetail?.total_paid_amount);
        const persistedOutstanding = toFiniteNumber(poFinanceDetail?.total_outstanding_amount ?? poDetail?.total_outstanding_amount);
        const persistedDownAmount = toFiniteNumber(poFinanceDetail?.total_down_amount ?? poDetail?.total_down_amount) ?? 0;
        const linkedQuoteTotal = resolveLinkedQuoteTotal(poDetail);

        const totalPayable = (() => {
            if (financeTotalPayable !== null) return financeTotalPayable;
            if (
                linkedQuoteTotal !== null &&
                linkedQuoteTotal > 0 &&
                (detailTotalPayable === null || Math.abs(detailTotalPayable - linkedQuoteTotal) > 0.01)
            ) {
                return linkedQuoteTotal;
            }
            if (detailTotalPayable !== null) return detailTotalPayable;
            return summaryValues.totalPayable;
        })();

        const paymentAdjustment = persistedTotalPaid !== null
            ? persistedTotalPaid - getPaymentTransactionsTotal(serverPaymentTransactions)
            : 0;
        const localPaidAmount = Math.max(
            getPaymentTransactionsTotal(paymentTransactions) + paymentAdjustment,
            0
        );
        const basePaidAmount = hasLocalPaymentTransactionChanges
            ? Number(localPaidAmount.toFixed(2))
            : Number((persistedTotalPaid ?? 0).toFixed(2));
        const baseOutstandingAmount = hasLocalPaymentTransactionChanges
            ? Number(Math.max(totalPayable - basePaidAmount, 0).toFixed(2))
            : Number((persistedOutstanding ?? 0).toFixed(2));

        return {
            totalPayable,
            persistedDownAmount,
            basePaidAmount,
            baseOutstandingAmount,
            maxDownAmount: Number(Math.max(persistedDownAmount + baseOutstandingAmount, 0).toFixed(2)),
        };
    }, [poFinanceDetail, poDetail, paymentTransactions, serverPaymentTransactions, hasLocalPaymentTransactionChanges, summaryValues.totalPayable]);

    const financeSummary = useMemo(
        () => {
            const rawCurrentDownAmount = toFiniteNumber(liveDownAmount) ?? 0;
            const currentDownAmount = Number(
                Math.min(rawCurrentDownAmount, financeBaseValues.maxDownAmount).toFixed(2)
            );
            const hasLiveDownAmountChange = Math.abs(currentDownAmount - financeBaseValues.persistedDownAmount) > 0.009;
            const totalPaidAmount = hasLiveDownAmountChange
                ? Number(Math.max(financeBaseValues.basePaidAmount + (currentDownAmount - financeBaseValues.persistedDownAmount), 0).toFixed(2))
                : financeBaseValues.basePaidAmount;
            const totalOutstandingAmount = hasLiveDownAmountChange
                ? Number(Math.max(financeBaseValues.totalPayable - totalPaidAmount, 0).toFixed(2))
                : financeBaseValues.baseOutstandingAmount;

            return {
                total_payable: financeBaseValues.totalPayable,
                total_down_amount: currentDownAmount,
                total_paid_amount: totalPaidAmount,
                total_outstanding_amount: totalOutstandingAmount,
            };
        },
        [financeBaseValues, liveDownAmount]
    );
    const currentOutstandingAmount = useMemo(
        () => Number(Math.max(toFiniteNumber(financeSummary?.total_outstanding_amount) ?? 0, 0).toFixed(2)),
        [financeSummary?.total_outstanding_amount]
    );
    const paymentTransactionAmountLimit = useMemo(() => {
        const currentTransactionAmount = isEditingPaymentTransaction
            ? Math.max(toFiniteNumber(editingTransaction?.amount) ?? 0, 0)
            : 0;

        return Number((currentOutstandingAmount + currentTransactionAmount).toFixed(2));
    }, [currentOutstandingAmount, editingTransaction?.amount, isEditingPaymentTransaction]);
    const canAddPaymentTransaction = currentOutstandingAmount > 0;
    const shouldShowPaymentLedger = paymentTransactions.length > 0 || attachments.length > 0;

    useEffect(() => {
        if (paymentTransactions.length > 0) {
            setFinanceUploadDate("");
            return;
        }

        if (attachments.length > 0 && !financeUploadDate) {
            setFinanceUploadDate(new Date().toLocaleDateString());
            return;
        }

        if (attachments.length === 0 && financeUploadDate) {
            setFinanceUploadDate("");
        }
    }, [attachments.length, paymentTransactions.length, financeUploadDate]);

    const validateDeliveryRows = () => {
        const nextErrors = {};

        deliveryTableData.forEach((row) => {
            const rowErrors = {};
            const expectedQty = Number(row.qty || 0);
            const qtyReceived = Number(row.qtyReceived || 0);
            const goodQty = Number(row.goodQty || 0);
            const damagedQty = Number(row.damagedQty || 0);
            const incorrectQty = Number(row.incorrectQty || 0);
            const totalBreakdown = goodQty + damagedQty + incorrectQty;

            if (qtyReceived > expectedQty) {
                rowErrors.qtyReceived = "Quantity Received cannot be greater than Expected Quantity.";
            }

            if (!row.warehouse) {
                rowErrors.warehouse = "Warehouse is required.";
            }

            if (goodQty > qtyReceived) {
                rowErrors.goodQty = "Good Quantity cannot be greater than Quantity Received.";
            }

            if (damagedQty > qtyReceived) {
                rowErrors.damagedQty = "Damaged Quantity cannot be greater than Quantity Received.";
            }

            if (incorrectQty > qtyReceived) {
                rowErrors.incorrectQty = "Incorrect Quantity cannot be greater than Quantity Received.";
            }

            if (totalBreakdown > qtyReceived) {
                rowErrors.breakdown = "Good + Damaged + Incorrect cannot be greater than Quantity Received.";
            }

            if (Object.keys(rowErrors).length > 0) {
                nextErrors[row.id] = rowErrors;
            }
        });

        setDeliveryErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSaveGeneralDetails = async (values, { setSubmitting }) => {
        const poId = resolveNumericPoId(poDetail?.id, id, values?.po_id, poDetail?.po_id, poDetail?.parts_purchase_order_id);
        if (!poId) {
            errorNotify("PO ID is missing.");
            setSubmitting(false);
            return;
        }

        const payload = {
            po_id: poId,
            notes: values?.notes || "",
        };

        if (activeTab === "finance") {
            const totalDownAmount = toFiniteNumber(liveDownAmount) ?? 0;
            if (totalDownAmount > financeBaseValues.maxDownAmount + 0.009) {
                errorNotify("Down payment cannot exceed the outstanding amount.");
                setSubmitting(false);
                return;
            }
            payload.total_down_amount = Number(totalDownAmount.toFixed(2));
            payload.files = getUploadedFileNames(attachments);
        }

        try {
            await dispatch(updatePurchaseOrderGeneralDetail(payload)).unwrap();
            if (activeTab === "finance") {
                setHasLocalFinanceAttachmentChanges(false);
            }
            successNotify("General details updated successfully.");
            navigateToPurchaseOrders(true);
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveDeliveryVerification = async (values, { setSubmitting }) => {
        const numericPoId = resolveNumericPoId(poDetail?.id, id, values?.po_id, poDetail?.po_id, poDetail?.parts_purchase_order_id);
        if (!numericPoId) {
            errorNotify("PO ID must be a valid number.");
            setSubmitting(false);
            return;
        }

        const isValid = validateDeliveryRows();
        if (!isValid) {
            errorNotify("Please resolve Delivery Verification errors in table.");
            setSubmitting(false);
            return;
        }

        const payload = {
            parts_purchase_order_id: numericPoId,
            notes: values?.notes || "",
            receiving_notes: values?.receiving_notes || "",
            sub_total: Number(summaryValues.subTotal || 0),
            total_shipping: Number(summaryValues.totalShipping || 0),
            total_tax: Number(costInputs.taxPercent || 0),
            total_discount: Number(costInputs.discountPercent || 0),
            total_payable: Number(summaryValues.totalPayable || 0),
            items: deliveryTableData
                .map((row) => ({
                    id: Number(row.id),
                    part_id: Number(row.part_id || row.part?.id),
                    actual_quantity: Number(row.qtyReceived || 0),
                    warehouse_id: Number(row.warehouse),
                    condition: row.condition || "good",
                    good_quantity: Number(row.goodQty || 0),
                    damaged_quantity: Number(row.damagedQty || 0),
                    incorrect_quantity: Number(row.incorrectQty || 0),
                }))
                .filter((item) =>
                    Number.isFinite(item.id) && item.id > 0 &&
                    Number.isFinite(item.part_id) && item.part_id > 0
                ),
        };

        if (!payload.items.length) {
            errorNotify("No valid PO items found for verification.");
            setSubmitting(false);
            return;
        }

        try {
            await dispatch(updatePurchaseOrderVerification(payload)).unwrap();
            successNotify("Delivery verification updated successfully.");
            dispatch(fetchPurchaseOrderDetail(id));
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveDetails = async (values, formikHelpers) => {
        if (activeTab === "general" || activeTab === "finance") {
            return handleSaveGeneralDetails(values, formikHelpers);
        }
        if (activeTab === "delivery") {
            return handleSaveDeliveryVerification(values, formikHelpers);
        }
        formikHelpers.setSubmitting(false);
    };

    const handleAddPaymentTransaction = async (values, { setSubmitting, resetForm }) => {
        const numericPoId = resolveNumericPoId(poDetail?.id, id, poDetail?.po_id, poDetail?.parts_purchase_order_id);
        if (!numericPoId) {
            errorNotify("PO ID is missing.");
            setSubmitting(false);
            return;
        }

        if (!canAddPaymentTransaction) {
            errorNotify("Add Transaction is disabled because there is no outstanding amount remaining.");
            setSubmitting(false);
            return;
        }

        const transactionAmount = toFiniteNumber(values.amount) ?? 0;
        if (transactionAmount > currentOutstandingAmount + 0.009) {
            errorNotify(`Transaction amount cannot exceed the outstanding amount of PKR ${formatFinanceAmount(currentOutstandingAmount)}.`);
            setSubmitting(false);
            return;
        }

        const uploadedModalFiles = (Array.isArray(values.fileObjects) ? values.fileObjects : [])
            .map((file) => file?.fileName || file?.name || file?.file_path)
            .filter(Boolean);

        const payload = {
            parts_purchase_order_id: numericPoId,
            amount: Number(transactionAmount),
            payment_method: values.payment_method,
            bank_name: values.payment_method === "bank_transfer" ? values.bank_name : undefined,
            account_title: values.payment_method === "bank_transfer" ? values.account_title : undefined,
            iban_number: values.payment_method === "bank_transfer" ? values.iban_number : undefined,
            reference_number: values.reference_number || undefined,
            files: uploadedModalFiles,
        };

        try {
            await dispatch(updatePurchaseOrderPaymentTransactions(payload)).unwrap();
            successNotify("Payment transaction added successfully.");
            closePaymentTransactionModal();
            resetForm();
            dispatch(fetchPurchaseOrderDetail(id));
            dispatch(fetchPurchaseOrderFinanceDetail(id));
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditPaymentTransaction = async (values, { setSubmitting, resetForm }) => {
        if (!editingTransaction?.id) {
            errorNotify("Transaction ID is missing.");
            setSubmitting(false);
            return;
        }

        const transactionAmount = toFiniteNumber(values.amount) ?? 0;
        if (transactionAmount > paymentTransactionAmountLimit + 0.009) {
            errorNotify(`Transaction amount cannot exceed the outstanding amount of PKR ${formatFinanceAmount(paymentTransactionAmountLimit)}.`);
            setSubmitting(false);
            return;
        }

        try {
            const uploadedModalFiles = mergeUniqueAttachments(values.fileObjects || [])
                .map((file) => file?.fileName || file?.name || file?.file_path)
                .filter(Boolean);
            const payload = {
                transaction_id: Number(editingTransaction.id),
                amount: Number(transactionAmount),
                payment_method: getPaymentMethodLabel(values.payment_method),
                bank_name: values.payment_method === "bank_transfer" ? values.bank_name : "",
                account_title: values.payment_method === "bank_transfer" ? values.account_title : "",
                iban_number: values.payment_method === "bank_transfer" ? values.iban_number : "",
                reference_number: values.reference_number || "",
                files: uploadedModalFiles,
            };

            await dispatch(updatePurchaseOrderPaymentTransactions(payload)).unwrap();
            successNotify("Payment transaction updated successfully.");
            closePaymentTransactionModal();
            resetForm();
            dispatch(fetchPurchaseOrderDetail(id));
            dispatch(fetchPurchaseOrderFinanceDetail(id));
        } catch (error) {
            errorNotify(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeletePaymentTransaction = async () => {
        if (!transactionToDelete?.id) {
            errorNotify("Transaction ID is missing.");
            return;
        }

        try {
            await dispatch(deletePurchaseOrderPaymentTransaction(transactionToDelete.id)).unwrap();
            successNotify("Payment transaction deleted successfully.");
            closeDeleteTransactionModal();
            dispatch(fetchPurchaseOrderDetail(id));
            dispatch(fetchPurchaseOrderFinanceDetail(id));
        } catch (error) {
            errorNotify(error);
        }
    };

    const handleDeliveryChange = (id, field, value) => {
        const normalizedValue = ["qtyReceived", "goodQty", "damagedQty", "incorrectQty"].includes(field)
            ? sanitizeNonNegative(value)
            : value;
        setHasLocalDeliveryChanges(true);
        setDeliveryTableData(prevData =>
            prevData.map(row =>
                row.id === id ? { ...row, [field]: normalizedValue } : row
            )
        );
        setDeliveryErrors((prev) => {
            if (!prev[id]) return prev;
            const updated = { ...prev };
            const rowErrors = { ...updated[id] };
            delete rowErrors[field];
            if (field === "goodQty" || field === "damagedQty" || field === "incorrectQty" || field === "qtyReceived") {
                delete rowErrors.breakdown;
                delete rowErrors.goodQty;
                delete rowErrors.damagedQty;
                delete rowErrors.incorrectQty;
            }
            if (Object.keys(rowErrors).length === 0) {
                delete updated[id];
            } else {
                updated[id] = rowErrors;
            }
            return updated;
        });
    };

    const deliveryColumns = [
        {
            name: "Part ID",
            selector: (row) => row?.part?.id,
            cell: (row) => {
                const partId = row?.part?.id || row?.part_id;
                return (
                    <span
                        style={{ color: "#4F19E2", textDecoration: "underline", cursor: partId ? "pointer" : "default" }}
                        onClick={() => handlePartDetailsNavigate(partId)}
                    >
                        {row?.part?.part_id || "-"}
                    </span>
                );
            },
            sortable: false,
            width: "120px"
        },
        { name: "Part Name", selector: (row) => row.name, sortable: false, wrap: true, width: "180px" },
        { name: "Expected Quantity", selector: (row) => row.qty, sortable: false, center: true },
        {
            name: "Quantity Received",
            cell: (row) => (
                <div style={{ width: "100%" }}>
                    <Input
                        type="number"
                        value={row.qtyReceived}
                        onChange={(e) => handleDeliveryChange(row.id, 'qtyReceived', e.target.value)}
                        placeholder=""
                        className="table_input"
                        required={false}
                        disabled={isPoVerified}

                    />
                    {deliveryErrors?.[row.id]?.qtyReceived && (
                        <div style={{ color: "#ff0000de", fontSize: "12px", marginTop: "3px" }}>
                            {deliveryErrors[row.id].qtyReceived}
                        </div>
                    )}
                </div>
            ),
            width: "150px"
        },
        {
            name: "Warehouse",
            cell: (row) => (
                <div style={{ width: "100%" }}>
                    <Select
                        options={warehouseOptions}
                        value={warehouseOptions.find(opt => opt.value === row.warehouse)}
                        onChange={(opt) => handleDeliveryChange(row.id, 'warehouse', opt?.value || "")}
                        placeholder="Select"
                        className="table_select"
                        menuPortalTarget={document.body}
                        customStyles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        isDisabled={isPoVerified}
                    />
                    {deliveryErrors?.[row.id]?.warehouse && (
                        <div style={{ color: "#ff0000de", fontSize: "12px", marginTop: "3px" }}>
                            {deliveryErrors[row.id].warehouse}
                        </div>
                    )}
                </div>
            ),
            width: "200px"
        },
        {
            name: "Condition",
            cell: (row) => (
                <div style={{ width: "100%" }}>
                    <Select
                        options={conditionOptions}
                        value={conditionOptions.find(opt => opt.value === row.condition)}
                        onChange={(opt) => handleDeliveryChange(row.id, 'condition', opt.value)}
                        placeholder="Select"
                        customStyles={{
                            ...getConditionStyles(row.condition),
                            menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                        className="condition_select"
                        menuPortalTarget={document.body}
                        isDisabled={isPoVerified}
                    />
                </div>
            ),
            width: "150px"
        },
        {
            name: "Good Quantity",
            cell: (row) => (
                <div style={{ width: "100%" }}>
                    <Input
                        type="number"
                        value={row.goodQty}
                        onChange={(e) => handleDeliveryChange(row.id, 'goodQty', e.target.value)}
                        placeholder=""
                        className="table_input"
                        required={false}
                        disabled={isPoVerified}
                    />
                    {deliveryErrors?.[row.id]?.goodQty && (
                        <div style={{ color: "#ff0000de", fontSize: "12px", marginTop: "3px" }}>
                            {deliveryErrors[row.id].goodQty}
                        </div>
                    )}
                </div>
            ),
            width: "150px"
        },
        {
            name: "Damaged Quantity",
            cell: (row) => (
                <div style={{ width: "100%" }}>
                    <Input
                        type="number"
                        value={row.damagedQty}
                        onChange={(e) => handleDeliveryChange(row.id, 'damagedQty', e.target.value)}
                        placeholder=""
                        className="table_input"
                        required={false}
                        disabled={isPoVerified}

                    />
                    {deliveryErrors?.[row.id]?.damagedQty && (
                        <div style={{ color: "#ff0000de", fontSize: "12px", marginTop: "3px" }}>
                            {deliveryErrors[row.id].damagedQty}
                        </div>
                    )}
                </div>
            ),
            width: "150px"
        },
        {
            name: "Incorrect Quantity",
            cell: (row) => (
                <div style={{ width: "100%" }}>
                    <Input
                        type="number"
                        value={row.incorrectQty}
                        onChange={(e) => handleDeliveryChange(row.id, 'incorrectQty', e.target.value)}
                        placeholder=""
                        className="table_input"
                        required={false}
                        disabled={isPoVerified}

                    />
                    {deliveryErrors?.[row.id]?.incorrectQty && (
                        <div style={{ color: "#ff0000de", fontSize: "12px", marginTop: "3px" }}>
                            {deliveryErrors[row.id].incorrectQty}
                        </div>
                    )}
                    {deliveryErrors?.[row.id]?.breakdown && (
                        <div style={{ color: "#ff0000de", fontSize: "12px", marginTop: "3px" }}>
                            {deliveryErrors[row.id].breakdown}
                        </div>
                    )}
                </div>
            ),
            width: "150px"
        },
    ];

    const columns = [
        {
            name: "Part ID",
            selector: (row) => row?.part?.id,
            cell: (row) => {
                const partId = row?.part?.id || row?.part_id;
                return (
                    <span
                        style={{ color: "#4F19E2", textDecoration: "underline", cursor: partId ? "pointer" : "default" }}
                        onClick={() => handlePartDetailsNavigate(partId)}
                    >
                        {row?.part?.part_id || "-"}
                    </span>
                );
            },
            sortable: false,
            minWidth: "120px",
        },
        { name: "Part Name", selector: (row) => row.name, sortable: false, wrap: true, minWidth: "280px", grow: 2 },
        { name: "Quantity", selector: (row) => row.qty, sortable: false, minWidth: "140px" },
        { name: "Unit Price (PKR)", selector: (row) => row.unitPrice, sortable: false, minWidth: "170px" },
        { name: "Total (PKR)", selector: (row) => row.total, sortable: false, minWidth: "140px" },
    ];

    // Pagination Logic
    const totalItems = partsData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / limit));
    const pageStart = (currentPage - 1) * limit;
    const pagedData = partsData.slice(pageStart, pageStart + limit);
    const pagedDeliveryData = deliveryTableData.slice(pageStart, pageStart + limit);

    const poTabs = [
        {
            key: "general",
            label: "General Details",
            icon: activeTab === "general" ? activeDetailsIcon : detailsIcon,
        },
        {
            key: "delivery",
            label: "Delivery Verification",
            icon: activeTab === "delivery" ? activeInspectionIcon : inspectionIcon,
        },
        {
            key: "finance",
            label: "Finance",
            icon: activeTab === "finance" ? coinsStackActiveIcon : coinsStackDarkIcon,
        },
    ];


    const disableCostSummaryInputs = activeTab !== "delivery" || isPoVerified;
    const saveDetailsLoading = activeTab === "delivery"
        ? updatePurchaseOrderVerificationLoading
        : updatePurchaseOrderGeneralDetailLoading;

    return (
        <Formik
            enableReinitialize={true}
            initialValues={formInitialValues}
            onSubmit={handleSaveDetails}
        >
            {({ values, setFieldValue, handleSubmit, isSubmitting }) => (
                <div className="po_details_page">
                    <Breadcrumbs values={["Procurement", "General Details"]} />

                    <div className="page_header">
                        <h2>Purchase Order Details</h2>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                submitText="Export PDF"
                                loadingText="Exporting..."
                                type="button"
                                shouldRow={false}
                                isShowCancel={false}
                                submitBtn={handleExportPdf}
                                loading={exportPdfLoading}
                            />
                            <PermissionWrapper
                                module={InventorySubModules.Procurement}
                                permission={ProcurementPermission.UpdatePo}
                                mainModule={AppMainModules.inventoryManagement}
                            >
                                <Button
                                    cancelBtn={() => navigateToPurchaseOrders()}
                                    submitText="Save Details"
                                    type="button"
                                    shouldRow={false}
                                    isShowSubmitBtn={true}
                                    submitBtn={handleSubmit}
                                    loading={saveDetailsLoading}
                                    disabled={saveDetailsLoading || poDetailLoading || isSubmitting}
                                />
                            </PermissionWrapper>
                        </div>
                    </div>

                    <div className="page_container">
                        <Row className="po_details_row">
                            <Col md={8}>
                                <div className="po_detail_bar">
                                    <span>Po ID: <strong>{values.po_id || poDetail?.parts_purchase_order_id || id}</strong></span>
                                </div>

                                <div className="po_detail_tabs">
                                    <CustomTabs tabs={poTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
                                </div>

                                {activeTab === "general" && (
                                    <>
                                        <div className="content_box">
                                            <h6>Parts Information</h6>
                                            <p>Review all parts included in this purchase order.</p>
                                            <NewTableComponent
                                                columns={columns}
                                                data={pagedData}
                                                currentPage={currentPage}
                                                setCurrentPage={setCurrentPage}
                                                limit={limit}
                                                setLimit={setLimit}
                                                totalItems={totalItems}
                                                totalPages={totalPages}
                                                showPagination={true}
                                                showFilter={false}
                                                searchFilters={searchFilters}
                                                setSearchFilters={setSearchFilters}
                                                paginationClassName="inline_pagination"
                                            />
                                        </div>

                                        <div className="content_box">
                                            <h6>Delivery Details</h6>
                                            <p>Delivery schedule and mode for this purchase order.</p>

                                            <div className="delivery_details_box">
                                                <div className="delivery_details_grid">
                                                    {/* <span className="delivery_label">Shipping Address</span>
                                            <span className="delivery_value">{poDetail?.vendor?.location || "N/A"}</span> */}

                                                    <span className="delivery_label">Delivery Type</span>
                                                    <span className="delivery_value">
                                                        {poDetail?.vendor_quote?.delivery_type
                                                            ? `${String(poDetail.vendor_quote.delivery_type)
                                                                .charAt(0)
                                                                .toUpperCase()}${String(poDetail.vendor_quote.delivery_type)
                                                                    .slice(1)
                                                                    .toLowerCase()}`
                                                            : "N/A"}
                                                    </span>

                                                    <span className="delivery_label">Delivery Date</span>
                                                    <span className="delivery_value">{poDetail?.vendor_quote?.delivery_date ? new Date(poDetail.vendor_quote.delivery_date).toLocaleDateString() : "N/A"}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="content_box">
                                            <h6>Vendor Information</h6>
                                            <p>Primary vendor and point-of-contact information.</p>

                                            <div className="vendor_info_row">
                                                <div style={{ flex: 1 }}>
                                                    <Input
                                                        label="Vendor Name"
                                                        placeholder="Enter Vendor Name"
                                                        disabled={true}
                                                        value={poDetail?.vendor?.name || ""}
                                                        required={true}
                                                        type="text"
                                                        className="w-100"
                                                    />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <Input
                                                        label="POC Name"
                                                        placeholder="Enter POC Name"
                                                        disabled={true}
                                                        value={poDetail?.vendor?.poc_name || ""}
                                                        required={true}
                                                        type="text"
                                                        className="w-100"
                                                    />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <Input
                                                        label="POC Contact"
                                                        placeholder="Enter POC Contact"
                                                        disabled={true}
                                                        value={poDetail?.vendor?.poc_contact || ""}
                                                        required={true}
                                                        type="text"
                                                        className="w-100"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {activeTab === "delivery" && (
                                    <>
                                        <div className="content_box">
                                            <h6>Delivery Verification</h6>
                                            <p>Verify delivery status and condition.</p>
                                            <NewTableComponent
                                                columns={deliveryColumns}
                                                data={pagedDeliveryData}
                                                currentPage={currentPage}
                                                setCurrentPage={setCurrentPage}
                                                limit={limit}
                                                setLimit={setLimit}
                                                totalItems={totalItems}
                                                totalPages={totalPages}
                                                showPagination={true}
                                                showFilter={false}
                                                searchFilters={searchFilters}
                                                setSearchFilters={setSearchFilters}
                                                paginationClassName="inline_pagination"
                                            />
                                        </div>

                                        <div className="content_box">
                                            <h6>Good Received Notes</h6>
                                            <p>Write any notes/observations during or after verification activity</p>
                                            <Input
                                                quill={true}
                                                name="receiving_notes"
                                                placeholder="Type GRN..."
                                                className="grn_input_wrapper"
                                                required={false}
                                                value={values.receiving_notes}
                                                setFieldValue={setFieldValue}
                                            />
                                        </div>
                                    </>
                                )}

                                {activeTab === "finance" && (
                                    <>
                                        <div className="content_box">
                                            <h6>Finance Information</h6>
                                            <p>Manage purchase order transactions, payment records, and supporting files. Upload relevant payment proofs or finance documents.</p>
                                            {/* <h1>Attachments</h1> */}
                                            {/* <p></p> */}
                                            <FileUpload
                                                files={attachments}
                                                setFiles={applyFinanceAttachmentUpdate}
                                                maxFileSize={10}
                                                destination="procurement"
                                                consumeUploadedFiles={!showAddTransactionModal}
                                            />
                                        </div>

                                        {shouldShowPaymentLedger && (
                                            <div className="content_box">
                                                <h6>Payment Ledger Summary</h6>
                                                <p>High-level overview of the financial health and obligations of the Purchase Order</p>

                                                <Row className="mb-3">
                                                    <Col md={4}>
                                                        <Input
                                                            label="Uploaded Date"
                                                            placeholder="YY-MM-DD"
                                                            type="text"
                                                            required={true}
                                                            showIcon={<FiCalendar size={16} />}
                                                            value={paymentTransactions?.[0]?.transaction_date
                                                                ? new Date(paymentTransactions[0].transaction_date).toLocaleDateString()
                                                                : financeUploadDate}
                                                            disabled={true}
                                                        />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input
                                                            label="Total Payable"
                                                            placeholder="00"
                                                            extraText="PKR"
                                                            showExtraText={true}
                                                            required={true}
                                                            disabled={true}
                                                            value={formatFinanceAmount(financeSummary.total_payable)}
                                                        />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input
                                                            label="Down Payment"
                                                            placeholder="00"
                                                            name="total_down_amount"
                                                            type="number"
                                                            extraText="PKR"
                                                            showExtraText={true}
                                                            required={true}
                                                            value={liveDownAmount}
                                                            onChange={(e) => {
                                                                const sanitized = sanitizeCostInput(e.target.value);
                                                                if (sanitized === null) return;

                                                                if (sanitized === "") {
                                                                    setLiveDownAmount("");
                                                                    setFieldValue("total_down_amount", "");
                                                                    return;
                                                                }

                                                                const numericDownAmount = toFiniteNumber(sanitized) ?? 0;
                                                                const nextValue = numericDownAmount > financeBaseValues.maxDownAmount + 0.009
                                                                    ? formatCostInput(financeBaseValues.maxDownAmount)
                                                                    : sanitized;

                                                                setLiveDownAmount(nextValue);
                                                                setFieldValue("total_down_amount", nextValue);
                                                            }}
                                                            disabled={poDetailLoading || saveDetailsLoading || isSubmitting}
                                                        />
                                                    </Col>
                                                </Row>

                                                <Row className="mb-4">
                                                    <Col md={4}>
                                                        <Input
                                                            label="Amount Paid"
                                                            placeholder="00"
                                                            extraText="PKR"
                                                            showExtraText={true}
                                                            required={true}
                                                            value={formatFinanceAmount(financeSummary.total_paid_amount)}
                                                            disabled={true}
                                                        />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input
                                                            label="Outstanding Amount"
                                                            placeholder="00"
                                                            extraText="PKR"
                                                            showExtraText={true}
                                                            required={true}
                                                            value={formatFinanceAmount(financeSummary.total_outstanding_amount)}
                                                            disabled={true}
                                                        />
                                                    </Col>
                                                </Row>

                                                <div className="finance_table_wrapper">
                                                    <table className="w-100 finance_table">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ color: '#888EA8', fontWeight: 500, fontSize: '13px', padding: '10px', textAlign: 'left', borderBottom: '1px solid #E0E6ED' }}>Transaction ID</th>
                                                                <th style={{ color: '#888EA8', fontWeight: 500, fontSize: '13px', padding: '10px', textAlign: 'left', borderBottom: '1px solid #E0E6ED' }}>Amount (PKR)</th>
                                                                <th style={{ color: '#888EA8', fontWeight: 500, fontSize: '13px', padding: '10px', textAlign: 'left', borderBottom: '1px solid #E0E6ED' }}>Payment Method</th>
                                                                <th style={{ color: '#888EA8', fontWeight: 500, fontSize: '13px', padding: '10px', textAlign: 'left', borderBottom: '1px solid #E0E6ED' }}>Reference Number</th>
                                                                <th style={{ color: '#888EA8', fontWeight: 500, fontSize: '13px', padding: '10px', textAlign: 'right', borderBottom: '1px solid #E0E6ED' }}>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {paymentTransactions.length ? paymentTransactions.map((transaction) => {
                                                                const transactionLabel = `T-${transaction?.id ?? "-"}`;

                                                                return (
                                                                    <tr key={transaction?.id ?? transactionLabel}>
                                                                        <td style={{ padding: '12px 10px', fontSize: '13px', color: '#3D3F5C', borderBottom: '1px solid #E0E6ED' }}>
                                                                            <span style={{ color: '#4F19E2', textDecoration: 'underline', cursor: 'pointer' }}>{transactionLabel}</span>
                                                                        </td>
                                                                        <td style={{ padding: '12px 10px', fontSize: '13px', color: '#3D3F5C', borderBottom: '1px solid #E0E6ED' }}>
                                                                            {formatFinanceAmount(transaction?.amount)}
                                                                        </td>
                                                                        <td style={{ padding: '12px 10px', fontSize: '13px', color: '#3D3F5C', borderBottom: '1px solid #E0E6ED', textTransform: "capitalize" }}>
                                                                            {getPaymentMethodLabel(transaction?.payment_method || "-") || "-"}
                                                                        </td>
                                                                        <td style={{ padding: '12px 10px', fontSize: '13px', color: '#3D3F5C', borderBottom: '1px solid #E0E6ED' }}>
                                                                            {transaction?.reference_number || "-"}
                                                                        </td>
                                                                        <td style={{ padding: '12px 10px', fontSize: '13px', color: '#3D3F5C', borderBottom: '1px solid #E0E6ED', textAlign: 'right' }}>
                                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px" }}>
                                                                                <FiEdit
                                                                                    size={16}
                                                                                    style={{ cursor: "pointer", color: "#1A4FBA" }}
                                                                                    onClick={() => handleOpenEditTransactionModal(transaction)}
                                                                                />
                                                                                <FiTrash2
                                                                                    size={16}
                                                                                    style={{ cursor: "pointer", color: "#DC2626" }}
                                                                                    onClick={() => handleOpenDeleteTransactionModal(transaction)}
                                                                                />
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            }) : (
                                                                <tr>
                                                                    <td colSpan={5} style={{ padding: '16px 10px', textAlign: 'center', color: '#888EA8', fontSize: '13px' }}>
                                                                        No payment transactions found.
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <PermissionWrapper
                                                    module={InventorySubModules.Procurement}
                                                    permission={ProcurementPermission.CreatePo}
                                                    mainModule={AppMainModules.inventoryManagement}
                                                >
                                                    <button
                                                        type="button"
                                                        className="add_transaction_btn"
                                                        style={{
                                                            border: '1px solid #E2E3E8',
                                                            borderRadius: '6px',
                                                            padding: '10px',
                                                            textAlign: 'center',
                                                            marginTop: '15px',
                                                            width: '100%',
                                                            color: canAddPaymentTransaction ? '#3D3F5C' : '#9CA3AF',
                                                            fontSize: '13px',
                                                            background: canAddPaymentTransaction ? '#fff' : '#F8FAFC',
                                                            fontWeight: 500,
                                                            cursor: canAddPaymentTransaction ? 'pointer' : 'not-allowed',
                                                            opacity: canAddPaymentTransaction ? 1 : 0.7,
                                                        }}
                                                        onClick={handleOpenAddTransactionModal}
                                                        disabled={!canAddPaymentTransaction}
                                                    >
                                                        + Add Transaction
                                                    </button>
                                                </PermissionWrapper>
                                            </div>
                                        )}
                                    </>
                                )}
                            </Col>

                            <Col md={4}>
                                <div className="content_box">
                                    {/* <div className="content_box"></div> */}
                                    <h6>Cost Summary</h6>
                                    <p>Financial breakdown of the service entries.</p>

                                    <div className="cost_summary_row">
                                        <span>Subtotal (Calculated)</span>
                                        <strong>PKR {formatFinanceAmount(summaryValues.subTotal)}</strong>
                                    </div>

                                    {/* <div className="cost_summary_row">
                                <span>Parts subtotal</span>
                                <input className="cost_input" value={formatFinanceAmount(summaryValues.subTotal)} disabled />
                            </div> */}

                                    <div className="cost_summary_row">
                                        <span>Freight/Shipping</span>
                                        <input
                                            className="cost_input"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={costInputs.shipping}
                                            onChange={(e) => handleCostInputChange("shipping", e.target.value)}
                                            placeholder="0.00"
                                            disabled={disableCostSummaryInputs}
                                        />
                                    </div>

                                    <div className="cost_summary_row">
                                        <span>Tax Amount (%)</span>
                                        <div className="cost_input_with_suffix">
                                            <input
                                                className="cost_input"
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={costInputs.taxPercent}
                                                onChange={(e) => handleCostInputChange("taxPercent", e.target.value)}
                                                placeholder="0.00"
                                                disabled={disableCostSummaryInputs}
                                            />
                                            <span className="cost_input_suffix">%</span>
                                        </div>
                                    </div>

                                    <div className="cost_summary_row">
                                        <span>Discount (%)</span>
                                        <div className="cost_input_with_suffix">
                                            <input
                                                className="cost_input"
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={costInputs.discountPercent}
                                                onChange={(e) => handleCostInputChange("discountPercent", e.target.value)}
                                                placeholder="0"
                                                disabled={disableCostSummaryInputs}
                                            />
                                            <span className="cost_input_suffix">%</span>
                                        </div>
                                    </div>

                                    <div className="cost_summary_row total">
                                        <span>Total</span>
                                        <span>PKR {formatFinanceAmount(summaryValues.totalPayable)}</span>
                                    </div>
                                </div>

                                <div className="po_right_card">
                                    <h5>General Notes</h5>
                                    <p>Add any additional notes or observations here.</p>
                                    <textarea
                                        rows="12"
                                        style={{
                                            width: '100%',
                                            border: '1px solid #E3E3E8',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            background: '#F9F9FB',
                                            color: '#3D3F5C',
                                            fontSize: '14px'
                                        }}
                                        value={values.notes}
                                        onChange={(e) => setFieldValue("notes", e.target.value)}
                                        placeholder="Write notes here..."
                                        disabled={activeTab !== "general"}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <CustomModal
                        show={showAddTransactionModal}
                        handleClose={closePaymentTransactionModal}
                        heading={isEditingPaymentTransaction ? "Edit Transaction" : "Add Transaction"}
                    >
                        <Formik
                            enableReinitialize={true}
                            initialValues={paymentTransactionModalInitialValues}
                            validationSchema={POPaymentTransactionSchema}
                            onSubmit={isEditingPaymentTransaction ? handleEditPaymentTransaction : handleAddPaymentTransaction}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                setFieldValue,
                                setFieldTouched,
                                handleSubmit,
                                isSubmitting,
                                submitCount,
                            }) => {
                                const modalAttachmentFiles = Array.isArray(values.fileObjects) ? values.fileObjects : [];
                                const transactionModalLabel = isEditingPaymentTransaction
                                    ? `T-${editingTransaction?.id ?? "-"}`
                                    : upcomingTransactionLabel;
                                const transactionSubmitLoading = updatePurchaseOrderPaymentTransactionsLoading;
                                const isTransactionSubmitting = isSubmitting || transactionSubmitLoading;

                                return (
                                    <form onSubmit={handleSubmit} className="px-1">
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <div style={{ background: "#F9F9FB", padding: "10px 15px", borderRadius: "6px", color: "#3D3F5C", display: "flex", alignItems: "center", gap: "5px" }}>
                                                    <span style={{ color: "#888EA8", fontSize: "13px" }}>Transaction ID:</span>
                                                    <span style={{ color: "#4F19E2", fontWeight: 500, fontSize: "13px" }}>{transactionModalLabel}</span>
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row className="mb-2">
                                            <Col md={12}>
                                                <Input
                                                    label="Amount"
                                                    required={true}
                                                    name="amount"
                                                    type="number"
                                                    placeholder="00"
                                                    extraText="PKR"
                                                    showExtraText={true}
                                                    value={values.amount}
                                                    onChange={(e) => {
                                                        const sanitized = sanitizeCostInput(e.target.value);
                                                        if (sanitized === null) return;

                                                        if (sanitized === "") {
                                                            setFieldValue("amount", "");
                                                            return;
                                                        }

                                                        const numericAmount = toFiniteNumber(sanitized) ?? 0;
                                                        const nextValue = numericAmount > paymentTransactionAmountLimit + 0.009
                                                            ? formatCostInput(paymentTransactionAmountLimit)
                                                            : sanitized;

                                                        setFieldValue("amount", nextValue);
                                                    }}
                                                    onBlur={handleBlur}
                                                    error={errors.amount}
                                                    touched={touched.amount}
                                                />
                                            </Col>
                                        </Row>

                                        <Row className="mb-2">
                                            <Col md={12}>
                                                <Input
                                                    label="Reference Number"
                                                    required={false}
                                                    name="reference_number"
                                                    type="text"
                                                    placeholder="TRX-123"
                                                    value={values.reference_number}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={errors.reference_number}
                                                    touched={touched.reference_number}
                                                />
                                            </Col>
                                        </Row>

                                        <Row className="mb-2">
                                            <Col md={12}>
                                                <Select
                                                    label="Payment Method"
                                                    placeholder="Select Payment Method"
                                                    options={paymentMethodOptions}
                                                    value={paymentMethodOptions.find((opt) => opt.value === values.payment_method) || null}
                                                    onChange={(opt) => {
                                                        const method = opt?.value || "";
                                                        setFieldValue("payment_method", method);
                                                        if (method !== "bank_transfer") {
                                                            setFieldValue("bank_name", "");
                                                            setFieldValue("account_title", "");
                                                            setFieldValue("iban_number", "");
                                                        }
                                                    }}
                                                    onBlur={handleBlur}
                                                    error={errors.payment_method}
                                                    touched={touched.payment_method}
                                                />
                                            </Col>
                                        </Row>

                                        {values.payment_method === "bank_transfer" && (
                                            <Row className="mb-2">
                                                <Col md={6}>
                                                    <Select
                                                        label="Bank"
                                                        placeholder="Select Bank"
                                                        options={banksDropdownData || []}
                                                        value={(banksDropdownData || []).find((opt) => opt.label === values.bank_name) || null}
                                                        onChange={(opt) => setFieldValue("bank_name", opt?.label || "")}
                                                        onBlur={handleBlur}
                                                        error={errors.bank_name}
                                                        touched={touched.bank_name}
                                                    />
                                                </Col>
                                                <Col md={6}>
                                                    <Input
                                                        label="Account Title"
                                                        name="account_title"
                                                        type="text"
                                                        placeholder="Enter Account Title"
                                                        value={values.account_title}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.account_title}
                                                        touched={touched.account_title}
                                                    />
                                                </Col>
                                                <Col md={12}>
                                                    <Input
                                                        label="IBAN Number"
                                                        name="iban_number"
                                                        type="text"
                                                        placeholder="PK00XXXX"
                                                        value={values.iban_number}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={errors.iban_number}
                                                        touched={touched.iban_number}
                                                    />
                                                </Col>
                                            </Row>
                                        )}

                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <label style={{ display: "block", fontSize: "13px", color: "#3D3F5C", marginBottom: "6px" }}>
                                                    Attachments {requiredStar}
                                                </label>
                                                <FileUpload
                                                    files={modalAttachmentFiles}
                                                    setFiles={(updater) => {
                                                        const baseFiles = modalAttachmentFiles || [];
                                                        const nextFiles = typeof updater === "function" ? updater(baseFiles) : updater;
                                                        setFieldValue("fileObjects", nextFiles);
                                                        setFieldValue("files", (nextFiles || []).map((file) => file?.fileName || file?.name).filter(Boolean));
                                                        setFieldTouched("fileObjects", true, false);
                                                    }}
                                                    maxFileSize={10}
                                                    destination="procurement"
                                                    consumeUploadedFiles={showAddTransactionModal}
                                                />
                                                {(touched.fileObjects || submitCount > 0) && errors.fileObjects && (
                                                    <div
                                                        className="error-message"
                                                        style={{
                                                            color: "#ff0000de",
                                                            fontSize: "12px",
                                                            marginTop: "3px",
                                                            marginLeft: "5px",
                                                        }}
                                                    >
                                                        {errors.fileObjects}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={3}>
                                                <button
                                                    type="button"
                                                    className="w-100"
                                                    style={{ background: "white", border: "1px solid #4F19E2", color: "#4F19E2", borderRadius: "6px", height: "40px", fontWeight: 500 }}
                                                    onClick={closePaymentTransactionModal}
                                                >
                                                    Cancel
                                                </button>
                                            </Col>
                                            <Col md={9}>
                                                <button
                                                    type="submit"
                                                    className="w-100"
                                                    disabled={isTransactionSubmitting}
                                                    style={{ background: "#1A4FBA", border: "none", color: "white", borderRadius: "6px", height: "40px", fontWeight: 500, opacity: isTransactionSubmitting ? 0.7 : 1 }}
                                                >
                                                    {isEditingPaymentTransaction
                                                        ? (isSubmitting ? "Saving..." : "Save Changes")
                                                        : (isTransactionSubmitting ? "Adding..." : "Add")}
                                                </button>
                                            </Col>
                                        </Row>
                                    </form>
                                );
                            }}
                        </Formik>
                    </CustomModal>

                    <ConfirmDeleteModal
                        show={showDeleteTransactionModal}
                        onClose={closeDeleteTransactionModal}
                        onConfirm={handleDeletePaymentTransaction}
                        loading={deletePurchaseOrderPaymentTransactionLoading}
                        title="Delete Transaction?"
                        description={transactionToDelete?.id
                            ? `This will permanently delete T-${transactionToDelete.id}.`
                            : "This action cannot be undone."}
                        confirmText="Delete"
                    />
                </div>
            )}
        </Formik>
    );
};

export default PODetails;
